import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { appraiserActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { StateSelect, ZipCodeInput, CountySelect, PhoneInput, InformModal } from '../../../common/SharedComponents';
import { CircularProgress } from '@material-ui/core';
import { AppraisalsApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import { handleAdminSuccess, handleAdminError } from '../../../common/SharedFunctions';
import { AppraiserFlagTypeOptions, AppraiserLicenseTypeOptions } from '../common/AppraiserConstants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';

const styles = {
    buttonMarginRight: { marginRight: '10px' },
    buttonContainer: { marginRight: '20px', textAlign: 'right' },
    form: { paddingLeft: '5px', paddingTop: '10px' },
    formRow: { marginBottom: '0' },
    appraiserRow: { marginBottom: '0', whiteSpace: 'nowrap' },
    parent: {
        position: 'relative',
        weight: '750px',
    },
    cover: {
        backgroundColor: 'rgba(0,0,0,0.4)',
        position: 'absolute',
        height: '100%',
        width: '100%',
        display: 'block',
        zIndex: '999',
        textAlign: 'center',
        cursor: 'wait',
    },
    messageBox: {
        backgroundColor: 'white',
        display: 'inline-block',
        width: '65%',
        height: '100px',
        marginTop: '200px',
        padding: '20px',
        borderRadius: '10px',
        cursor: 'wait',
    },
    appraiserList: { float: 'left', width: '90%' },
    addButton: { height: '38px', float: 'left', marginLeft: '5px' },
    sectionHeader: { margin: '5px 10px' },
};

const AppraisersModal = () => {
    const { isOpen, appraiserId, onSave } = useSelector((state) => state.appraisers.appraiserModal);
    const { dispatch } = store;

    const [companyName, set_companyName] = useState('');
    const [firstName, set_firstName] = useState('');
    const [lastName, set_lastName] = useState('');
    const [email, set_email] = useState('');
    const [phone, set_phone] = useState('');
    const [extension, set_extension] = useState('');
    const [appraiserLicenseTypeId, set_licenseType] = useState('');
    const [appraiserFlagTypeId, set_flagType] = useState('');
    const [firstEngagedDate, set_firstEngagedDate] = useState(new Date());
    const [ofacCheckCompletedDate, set_ofacCheckCompletedDate] = useState(new Date());
    const [address_street1, set_address_street1] = useState('');
    const [address_street2, set_address_street2] = useState('');
    const [address_city, set_address_city] = useState('');
    const [address_state, set_address_state] = useState('');
    const [address_zipCode, set_address_zipCode] = useState('');
    const [address_county, set_address_county] = useState('');

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');
    const [openNamesNotUnique, set_openNamesNotUnique] = useState(false);

    const appraisersApi = new AppraisalsApi.AppraisalsApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const _handleClose = () =>
        dispatch(
            appraiserActions.setAppraiserModal({
                appraiserModal: { isOpen: false },
            })
        );

    const _saveAppraiser = async (e) => {
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            //Ensure names are unique...
            var names = {
                companyName,
                firstName,
                lastName,
                ignoreId: appraiserId,
            };

            const nameResponse = await appraisersApi.validateNames(names);
            if (nameResponse.statusCode !== 200) {
                return;
            }

            if (!nameResponse.result) {
                set_openNamesNotUnique(true);
                set_loadingOpen(false);
                return;
            }

            const model = {
                id: appraiserId,
                companyName,
                firstName,
                lastName,
                email,
                phone,
                extension,
                appraiserLicenseTypeId,
                appraiserFlagTypeId,
                firstEngagedDate,
                ofacCheckCompletedDate,
                mailingAddress: {
                    street1: address_street1,
                    street2: address_street2,
                    city: address_city,
                    state: address_state,
                    zipCode: address_zipCode,
                    county: address_county,
                },
            };

            const response = await appraisersApi.addUpdateAppraiser(model);
            if (response.statusCode !== 200) {
                return;
            }

            const savedAppraiser = response.result;
            if (savedAppraiser.id <= 0) {
                throw new Error('Failed to save new Appraiser!');
            }
            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Appraiser successfully saved.');
            set_loadingOpen(false);

            if (onSave) {
                onSave(savedAppraiser.id);
            }

            setTimeout(() => {
                dispatch(
                    appraiserActions.setAppraiserModal({
                        appraiserModal: { isOpen: false },
                    })
                );
            }, 800);
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    useEffect(() => {
        async function isOpenChanged() {
            if (!isOpen) {
                return;
            }

            let appraiser = null;
            if (appraiserId && appraiserId > 0) {
                const response = await appraisersApi.getAppraiser(appraiserId);
                if (response.statusCode !== 200) {
                    return;
                }
                appraiser = response.result;
            }

            if (!appraiser) {
                appraiser = {
                    companyName: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    extension: '',
                    appraiserLicenseTypeId: '',
                    appraiserFlagTypeId: '',
                    firstEngagedDate: '',
                    ofacCheckCompletedDate: '',
                    mailingAddress: {
                        street1: '',
                        street2: '',
                        city: '',
                        state: '',
                        zipCode: '',
                        county: '',
                    },
                };
            }

            set_companyName(appraiser.companyName ?? '');
            set_firstName(appraiser.firstName ?? '');
            set_lastName(appraiser.lastName ?? '');
            set_email(appraiser.email ?? '');
            set_phone(appraiser.phone ?? '');
            set_extension(appraiser.extension ?? '');
            set_licenseType(appraiser.appraiserLicenseTypeId ?? '');
            set_flagType(appraiser.appraiserFlagTypeId ?? '');
            set_firstEngagedDate(appraiser.firstEngagedDate ?? '');
            set_ofacCheckCompletedDate(appraiser.ofacCheckCompletedDate ?? '');
            set_address_street1(appraiser.mailingAddress.street1 ?? '');
            set_address_street2(appraiser.mailingAddress.street2 ?? '');
            set_address_city(appraiser.mailingAddress.city ?? '');
            set_address_state(appraiser.mailingAddress.state ?? '');
            set_address_zipCode(appraiser.mailingAddress.zipCode ?? '');
            set_address_county(appraiser.mailingAddress.county ?? '');

            set_loadingOpen(false);
        }

        isOpenChanged();
    }, [isOpen, appraiserId]);

    return (
        <Dialog
            maxWidth='sm'
            open={isOpen}
            onClose={_handleClose}
            aria-labelledby='form-dialog-title'
        >
            <DialogContent>
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '800px' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div style={styles.parent}>
                                {loadingOpen && (
                                    <div style={styles.cover}>
                                        <div style={styles.messageBox}>
                                            <CircularProgress />
                                            <br />
                                            {loadingMessage}
                                        </div>
                                    </div>
                                )}
                                <form
                                    name='appraiserForm'
                                    onSubmit={(event) => _saveAppraiser(event)}
                                >
                                    <div className='scrollable-content'>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1>Appraiser Detail</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div
                                            style={styles.form}
                                            className={'standard-form'}
                                        >
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-12'>
                                                    <label htmlFor='companyName'>
                                                        Company
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        id='companyName'
                                                        type='text'
                                                        name='companyName'
                                                        className='req'
                                                        required
                                                        value={companyName}
                                                        onChange={(e) => set_companyName(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-6'>
                                                    <label htmlFor='firstName'>First Name</label>
                                                    <input
                                                        id='firstName'
                                                        type='text'
                                                        name='firstName'
                                                        className='req'
                                                        value={firstName}
                                                        onChange={(e) => set_firstName(e.target.value)}
                                                    />
                                                </div>
                                                <div className='columns small-12 large-6'>
                                                    <label htmlFor='lastName'>Last Name</label>
                                                    <input
                                                        id='lastName'
                                                        type='text'
                                                        name='lastName'
                                                        className='req'
                                                        value={lastName}
                                                        onChange={(e) => set_lastName(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-12'>
                                                    <label htmlFor='email'>
                                                        Email
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        id='email'
                                                        type='email'
                                                        name='email'
                                                        className='req'
                                                        required
                                                        value={email}
                                                        onChange={(e) => set_email(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-4'>
                                                    <label htmlFor='phone'>
                                                        Phone
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <PhoneInput
                                                        id='phone'
                                                        value={phone}
                                                        onChange={(value) => set_phone(value)}
                                                        required
                                                    />
                                                </div>
                                                <div className='columns small-12 large-4'>
                                                    <label htmlFor='extension'>Extension</label>
                                                    <input
                                                        id='extension'
                                                        type='text'
                                                        name='extension'
                                                        value={extension}
                                                        onChange={(e) => set_extension(e.target.value)}
                                                    />
                                                </div>
                                                <div className='columns small-12 large-4'>
                                                    <label htmlFor='licenseType'>
                                                        License Type
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <select
                                                        id='licenseType'
                                                        value={appraiserLicenseTypeId}
                                                        onChange={(event) => set_licenseType(event.target.value)}
                                                        required
                                                    >
                                                        <option value=''>Select</option>
                                                        {AppraiserLicenseTypeOptions.map((type) => {
                                                            return (
                                                                <option
                                                                    key={type.id}
                                                                    value={type.id}
                                                                >
                                                                    {type.name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 medium-6 large-4'>
                                                    <label htmlFor='flagType'>Flag</label>
                                                    <select
                                                        id='flagType'
                                                        value={appraiserFlagTypeId}
                                                        onChange={(event) => set_flagType(event.target.value)}
                                                    >
                                                        <option value=''>Select</option>
                                                        {AppraiserFlagTypeOptions.map((type) => {
                                                            return (
                                                                <option
                                                                    key={type.id}
                                                                    value={type.id}
                                                                >
                                                                    {type.name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                                <div className='columns small-12 medium-6 large-4'>
                                                    <label htmlFor='firstEngagedDate'>Date First Engaged</label>
                                                    <DatePicker
                                                        id='firstEngagedDate'
                                                        selected={firstEngagedDate ? new Date(firstEngagedDate) : null}
                                                        onChange={(data) => set_firstEngagedDate(data)}
                                                    />
                                                    <span className='helper'>
                                                        <span className='tooltip-trigger'>
                                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                                        </span>
                                                        <span className='text'>e.g. 12/01/1995</span>
                                                    </span>
                                                </div>
                                                <div className='columns small-12 medium-6 large-4'>
                                                    <label htmlFor='ofacCheckCompletedDate'>Date OFAC Check Completed</label>
                                                    <DatePicker
                                                        id='ofacCheckCompletedDate'
                                                        selected={
                                                            ofacCheckCompletedDate ? new Date(ofacCheckCompletedDate) : null
                                                        }
                                                        onChange={(data) => set_ofacCheckCompletedDate(data)}
                                                    />
                                                    <span className='helper'>
                                                        <span className='tooltip-trigger'>
                                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                                        </span>
                                                        <span className='text'>e.g. 12/01/1995</span>
                                                    </span>
                                                </div>
                                            </div>
                                            <h2
                                                className='decorated'
                                                style={styles.sectionHeader}
                                            >
                                                Address
                                            </h2>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-6'>
                                                    <label htmlFor='address_street1'>
                                                        Street Address
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        id='address_street1'
                                                        type='text'
                                                        value={address_street1}
                                                        onChange={(event) => set_address_street1(event.target.value)}
                                                        required
                                                    />
                                                </div>

                                                <div className='columns small-12 large-6'>
                                                    <label htmlFor='address_street2'>Street Address 2</label>
                                                    <input
                                                        id='address_street2'
                                                        type='text'
                                                        value={address_street2}
                                                        onChange={(event) => set_address_street2(event.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-6'>
                                                    <label htmlFor='address_city'>
                                                        City
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        id='address_city'
                                                        type='text'
                                                        value={address_city}
                                                        onChange={(event) => set_address_city(event.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className='columns small-12 medium-6 large-3'>
                                                    <label htmlFor='address_state'>
                                                        State
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <StateSelect
                                                        id='address_state'
                                                        value={address_state}
                                                        onChange={(event) => set_address_state(event.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className='columns small-12 medium-6 large-3'>
                                                    <label htmlFor='address_zipCode'>
                                                        Zip Code
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <ZipCodeInput
                                                        id='address_zipCode'
                                                        value={address_zipCode}
                                                        onChange={(event) => set_address_zipCode(event.target.value)}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-6'>
                                                    <label htmlFor={'county'}>County</label>
                                                    <CountySelect
                                                        id={'county'}
                                                        value={address_county ?? ''}
                                                        selectedState={address_state ?? ''}
                                                        onChange={(event) => set_address_county(event.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                                <InformModal
                                    isOpen={openNamesNotUnique}
                                    title='Duplicate Appraiser!'
                                    text='The Company, First and Last names that you have entered already exist in the database.'
                                    confirmCallback={() => set_openNamesNotUnique(false)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AppraisersModal;
